import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { useBoundValue } from "../hooks/use-bound-value"
import { Slider } from "../components/ui/slider"

export function SliderRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent } = useRenderContext()
  // Theme-v2 atom defaults: theme-variant/size surfaced on data-* attrs only.
  // Legacy pre-v2 slider blocks overload `element.variant`/`element.size` as
  // numeric min/max carriers; those semantics are preserved unchanged below
  // and remain separate from the theme-v2 data-variant/data-size surface.
  const atomDefaults = useAtomDefaults("slider")
  const resolvedVariant = atomDefaults.defaultVariant ?? "default"
  const resolvedSize = atomDefaults.defaultSize ?? "default"

  const min = element.min != null ? Number(element.min) : (element.variant ? Number(resolveSymbol(element.variant) ?? 0) : 0)
  const max = element.max != null ? Number(element.max) : (element.size ? Number(resolveSymbol(element.size) ?? 100) : 100)
  const defaultVal = element.value != null ? Number(element.value) : (element.defaultValue != null ? Number(element.defaultValue) : min)

  const [value, setValue] = useBoundValue<number>(
    element.bind,
    resolveSymbol,
    defaultVal,
    Number,
  )

  const handleValueChange = (vals: number | readonly number[]) => {
    const first = Array.isArray(vals) ? (vals as readonly number[])[0] : (vals as number)
    setValue(first)
    dispatchEvent(element.id, "change", { value: first })
  }

  return (
    <div className={element.className} data-testid={`slider-${element.id}`} data-action="input" data-field-type="slider" data-atom-type="slider" data-variant={resolvedVariant} data-size={resolvedSize}>
      <Slider
        id={element.id}
        min={min}
        max={max}
        value={[value]}
        onValueChange={handleValueChange}
      />
    </div>
  )
}
